import React, { Component, useState, useEffect, ErrorInfo } from 'react';
import { auth, db } from './lib/firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import AuthForm from './components/AuthForm';
import Dashboard from './components/Dashboard';
import { useHousehold } from './hooks/useHousehold';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    (this as any).state = { hasError: false, error: null, errorInfo: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error, errorInfo: null };
  }
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    (this as any).setState({ errorInfo });
    console.error("ErrorBoundary caught an error", error, errorInfo);
  }
  render() {
    if ((this as any).state.hasError) {
      return (
        <div className="p-8 text-red-600 bg-red-50 h-screen w-screen overflow-auto">
          <h1 className="text-2xl font-bold mb-4">Something went wrong.</h1>
          <pre className="bg-white p-4 rounded shadow text-sm whitespace-pre-wrap">{(this as any).state.error && (this as any).state.error.toString()}</pre>
          <br />
          <h2 className="font-bold">Component Stack:</h2>
          <pre className="bg-white p-4 rounded shadow text-xs whitespace-pre-wrap">{(this as any).state.errorInfo?.componentStack}</pre>
        </div>
      );
    }
    return (this as any).props.children;
  }
}

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  
  const { householdId, loading: householdLoading, error: householdError, retry: retryHousehold } = useHousehold(user);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setAuthLoading(false);
    });

    return unsubscribe;
  }, []);

  if (authLoading || (user && householdLoading)) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-slate-100">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  // A signed-in user whose account failed to load used to silently fall
  // through to <AuthForm/> below — looking exactly like the sign-in
  // itself had failed, even though they were still authenticated. Show
  // what actually happened instead, with a way to retry without needing
  // to sign in again.
  if (user && !householdId && householdError) {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center bg-slate-100 gap-4 p-6 text-center">
        <p className="text-slate-800 font-semibold">We couldn't load your account.</p>
        <p className="text-sm text-slate-500 max-w-sm">{householdError}</p>
        <button
          onClick={retryHousehold}
          className="px-5 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="h-screen w-screen bg-slate-100 flex overflow-hidden font-sans text-slate-800 selection:bg-blue-200 selection:text-blue-900">
        {user && householdId ? <Dashboard user={user} householdId={householdId} /> : <AuthForm />}
      </div>
    </ErrorBoundary>
  );
}
